import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  getReferralSettingsList,
  updateReferralSettings,
  getCategoryEventList,
  getRewardEventList,
  createRewardEvent,
  updateRewardEvent,
  getReferralLevelList,
  createReferralLevel,
  updateReferralLevel,
  getAuditLogList,
} from "services/rewardsServices";
import {
  applyEventFieldUpdate,
  applyEarningFrequencyUpdate,
  applyEventToggle,
  applyReferralLevelFieldUpdate,
  applyReferralLevelToggle,
  buildCategoryOrderFromEvents,
  buildEventConfigMap,
  extractGlobalSettingsFromListResponse,
  extractGlobalSettingsFromUpdateResponse,
  mergeEventAfterUpdate,
  mergeReferralLevelAfterUpdate,
  mapEventFromMutationResponse,
  mapEventsFromListResponse,
  mapEventToCreateApi,
  mapEventToUpdateApi,
  mapEarningFrequencyPayload,
  getEventSaveSignature,
  getGlobalSettingsSaveSignature,
  getReferralLevelSaveSignature,
  mapGlobalSettingsToApi,
  mapReferralLevelFromMutationResponse,
  mapReferralLevelsFromListResponse,
  mapReferralLevelToCreateApi,
  mapReferralLevelToUpdateApi,
  extractAuditLogPagination,
  getAuditLogListPayload,
  mapAuditLogsFromListResponse,
  mapCategoryEventNamesFromResponse,
} from "./rewardsMappers";
import {
  DEFAULT_UI_FREQUENCY_OPTIONS,
  UI_FREQUENCY,
  UI_FREQUENCY_LABELS,
  buildFrequencyOptionsFromEvents,
  getLimitsMode,
  getLimitsModeForUiKey,
  getUiFrequencyKey,
  isBackendHardcodedFrequencyEvent,
} from "./rewardsConstants";
import {
  applyEventLimitBlur,
  coinDraftToNumber,
  fmtTime,
  finalizeDailyLimit,
  finalizeMonthlyLimit,
  finalizeExpiryDays,
  normalizeCoinDraft,
  parseWholeNumber,
  validateEvent,
  validateEventCaps,
  validateEventExpiry,
  validateReferralLevel,
  deriveLevelStatus,
} from "./rewardsUtils";
import "./RewardsEngine.css";

const DEFAULT_ACTIVE_COINS = 10;

function cloneState(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function signaturesEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function isNumericField(el) {
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.type === "number") return true;
  return el.inputMode === "numeric" || el.classList.contains("coins");
}

function GlobalSettingsSection({ settings, loading, saving, onChange }) {
  if (loading) {
    return (
      <div className="re-card re-settings-card">
        <div className="re-settings-card-title">Global Settings</div>
        <div className="re-loading">Loading global settings…</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="re-card re-settings-card">
        <div className="re-settings-card-title">Global Settings</div>
        <div className="re-empty-note">Unable to load global settings.</div>
      </div>
    );
  }

  const handleEncashmentLock = (e) => {
    onChange({ ...settings, encashmentLockDays: normalizeCoinDraft(e.target.value) });
  };

  const handleRedemptionPercent = (e) => {
    const val = parseWholeNumber(e.target.value, { min: 0, max: 100 });
    onChange({ ...settings, redemptionPercent: val });
  };

  const handleReferralCap = (e) => {
    const val = normalizeCoinDraft(e.target.value);
    onChange({ ...settings, monthlyReferralCoinCap: val });
  };

  return (
    <div className="re-card re-settings-card">
      <div className="re-settings-card-title">
        Global Settings
        {saving && <span className="re-saving-note"> · Saving…</span>}
      </div>
      <div className="re-settings-row">
        <div>
          <div className="re-settings-label">Encashment lock (days)</div>
          <div className="re-settings-desc">
            Redemption is blocked unless the professional has an approved/completed shift within this many days
          </div>
        </div>
        <div className="re-settings-input-group">
          <input
            type="text"
            inputMode="numeric"
            style={{ width: 84 }}
            value={settings.encashmentLockDays}
            onChange={handleEncashmentLock}
          />
        </div>
      </div>
      <div className="re-settings-row">
        <div>
          <div className="re-settings-label">Percentage of coin redemption</div>
          <div className="re-settings-desc">
            Share of a professional&apos;s coin balance that can be redeemed at once
          </div>
        </div>
        <div className="re-settings-input-group">
          <input
            type="number"
            style={{ width: 84 }}
            value={settings.redemptionPercent}
            onChange={handleRedemptionPercent}
          />
          <span className="re-pct-suffix">%</span>
        </div>
      </div>
      <div className="re-settings-row">
        <div>
          <div className="re-settings-label">Monthly referral coin cap</div>
          <div className="re-settings-desc">
            Program-wide cap, separate from individual event caps
          </div>
        </div>
        <div className="re-settings-input-group">
          <input
            type="text"
            inputMode="numeric"
            style={{ width: 84 }}
            value={settings.monthlyReferralCoinCap}
            onChange={handleReferralCap}
          />
        </div>
      </div>
    </div>
  );
}

function LimitCell({ eventKey, cfg, onUpdate, onLimitBlur, readOnly = false }) {
  if (isBackendHardcodedFrequencyEvent(eventKey)) {
    return <span className="re-locked-note">—</span>;
  }

  const limitsMode = getLimitsMode(cfg);

  if (readOnly) {
    if (limitsMode === "daily" || limitsMode === "daily_monthly") {
      return (
        <span className="re-limit-group">
          {cfg.dailyLimit ?? "—"} <span className="re-limit-unit">/day</span>
          {limitsMode === "daily_monthly" && (
            <>
              {" "}
              {cfg.monthlyLimit ?? "—"} <span className="re-limit-unit">/mo</span>
            </>
          )}
        </span>
      );
    }
    if (limitsMode === "monthly") {
      return (
        <span className="re-limit-group">
          {cfg.monthlyLimit ?? "—"} <span className="re-limit-unit">/mo</span>
        </span>
      );
    }
    if (limitsMode === "cooldown") {
      return (
        <span className="re-limit-group">
          every {cfg.cooldownDays ?? "—"} days
        </span>
      );
    }
    return <span className="re-locked-note">—</span>;
  }

  if (limitsMode === "daily" || limitsMode === "daily_monthly") {
    return (
      <div className="re-limit-group">
        <input
          type="text"
          inputMode="numeric"
          className="re-cell-input re-limit-input"
          placeholder="—"
          title="Max earnings per calendar day (resets at midnight IST)"
          value={cfg.dailyLimit ?? ""}
          onChange={(e) => onUpdate(eventKey, "dailyLimit", e.target.value)}
          onBlur={(e) => onLimitBlur?.(eventKey, "dailyLimit", e.target.value)}
        />
        <span className="re-limit-unit">/day</span>
        {limitsMode === "daily_monthly" && (
          <>
            <input
              type="text"
              inputMode="numeric"
              className="re-cell-input re-limit-input"
              placeholder="—"
              title="Must be greater than daily limit"
              value={cfg.monthlyLimit ?? ""}
              onChange={(e) => onUpdate(eventKey, "monthlyLimit", e.target.value)}
              onBlur={(e) => onLimitBlur?.(eventKey, "monthlyLimit", e.target.value)}
            />
            <span className="re-limit-unit">/mo</span>
          </>
        )}
      </div>
    );
  }
  if (limitsMode === "monthly") {
    return (
      <div className="re-limit-group">
        <input
          type="number"
          className="re-cell-input re-limit-input"
          placeholder="—"
          title="Per calendar month — resets on the 1st"
          value={cfg.monthlyLimit ?? ""}
          onChange={(e) => onUpdate(eventKey, "monthlyLimit", e.target.value)}
        />
        <span className="re-limit-unit">/mo</span>
      </div>
    );
  }
  if (limitsMode === "cooldown") {
    return (
      <div className="re-limit-group">
        every{" "}
        <input
          type="text"
          inputMode="numeric"
          className="re-cell-input re-limit-input"
          value={cfg.cooldownDays === "" || cfg.cooldownDays == null ? "" : String(cfg.cooldownDays)}
          onChange={(e) => onUpdate(eventKey, "cooldownDays", e.target.value)}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            if (raw === "") {
              onUpdate(eventKey, "cooldownDays", 90);
              return;
            }
            onUpdate(eventKey, "cooldownDays", String(parseWholeNumber(raw, { min: 1 })));
          }}
        />{" "}
        days
      </div>
    );
  }
  return <span className="re-locked-note">—</span>;
}

const HARDCODED_FREQUENCY_INFO =
  "This event's frequency and limits are handled in the backend and cannot be changed here.";

function HardcodedFrequencyInfoButton() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  return (
    <div className="re-info-wrap" ref={rootRef}>
      <button
        type="button"
        className="re-info-btn"
        aria-expanded={open}
        aria-label="Backend hardcoded frequency info"
        onClick={() => setOpen((prev) => !prev)}
      >
        i
      </button>
      {open && (
        <div className="re-info-popover" role="tooltip">
          {HARDCODED_FREQUENCY_INFO}
        </div>
      )}
    </div>
  );
}

function ModalScrollSelect({ value, options, onChange, disabled, placeholder = "Select…" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const display = value || placeholder;

  return (
    <div className="re-modal-scroll-select" ref={rootRef}>
      <button
        type="button"
        className="re-modal-input re-modal-scroll-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <span className="re-modal-scroll-select-value">{display}</span>
      </button>
      {open && options.length > 0 && (
        <ul className="re-modal-scroll-select-menu" role="listbox">
          {options.map((opt) => (
            <li key={opt}>
              <button
                type="button"
                role="option"
                aria-selected={value === opt}
                className={`re-modal-scroll-select-option${value === opt ? " selected" : ""}`}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function createEmptyAddEventForm(categoryNames = []) {
  return {
    name: "",
    desc: "",
    category: categoryNames[0] || "",
    coins: "",
    uiFrequencyKey: UI_FREQUENCY.ONCE,
    dailyLimit: "",
    monthlyLimit: "",
    cooldownDays: "",
    expiryDays: "1",
  };
}

function AddEventModal({
  open,
  form,
  categoryNames,
  categoriesLoading,
  saving,
  onChange,
  onLimitBlur,
  onCancel,
  onSubmit,
}) {
  const [descTouched, setDescTouched] = useState(false);
  const [expiryTouched, setExpiryTouched] = useState(false);
  const limitsMode = getLimitsModeForUiKey(form.uiFrequencyKey);
  const descMissing = !(form.desc || "").trim();
  const expiryWarnings = validateEventExpiry({ expiryDays: form.expiryDays });
  const expiryInvalid = expiryWarnings.length > 0;

  useEffect(() => {
    if (open) {
      setDescTouched(false);
      setExpiryTouched(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="re-modal-overlay" onMouseDown={onCancel}>
      <div className="re-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="re-modal-close" onClick={onCancel} aria-label="Close">
          ×
        </button>
        <div className="re-modal-title">Add Event</div>
        <div className="re-modal-sub">Configure a new rewards event. Event key must be unique.</div>

        <form
          onSubmit={(e) => {
            if (descMissing) {
              e.preventDefault();
              setDescTouched(true);
              toast.error("Reward History Name is required.");
              return;
            }
            if (expiryInvalid) {
              e.preventDefault();
              setExpiryTouched(true);
              toast.error(expiryWarnings[0]);
              return;
            }
            onSubmit(e);
          }}
        >
          <div className="re-modal-field">
            <label className="re-modal-label">Event name</label>
            <input
              type="text"
              className="re-modal-input"
              placeholder="e.g. Locum Job Application Submitted"
              value={form.name}
              autoFocus
              onChange={(e) => onChange("name", e.target.value)}
            />
          </div>

          <div className="re-modal-field">
            <label className="re-modal-label">Reward History Name</label>
            <input
              type="text"
              className={`re-modal-input${descMissing && descTouched ? " re-modal-input-error" : ""}`}
              placeholder="Shown in Rewards History on the app"
              value={form.desc}
              onChange={(e) => onChange("desc", e.target.value)}
              onBlur={() => setDescTouched(true)}
            />
            {descMissing && descTouched ? (
              <div className="re-modal-error">Reward History Name is required.</div>
            ) : (
              <div className="re-modal-hint">This value is shown in Rewards History on the app.</div>
            )}
          </div>

          <div className="re-modal-field">
            <label className="re-modal-label">Category</label>
            <ModalScrollSelect
              value={form.category}
              options={categoryNames}
              onChange={(cat) => onChange("category", cat)}
              disabled={categoriesLoading || categoryNames.length === 0}
              placeholder={
                categoriesLoading
                  ? "Loading categories…"
                  : categoryNames.length === 0
                    ? "No categories available"
                    : "Select category…"
              }
            />
          </div>

          <div className="re-modal-row">
            <div className="re-modal-field">
              <label className="re-modal-label">Coins</label>
              <input
                type="text"
                inputMode="numeric"
                className="re-modal-input"
                placeholder="0"
                value={form.coins}
                onChange={(e) => onChange("coins", normalizeCoinDraft(e.target.value))}
              />
              <div className="re-modal-hint">0 pauses the event (no payout).</div>
            </div>

            <div className="re-modal-field">
              <label className="re-modal-label">Frequency</label>
              <select
                className="re-modal-input"
                value={form.uiFrequencyKey}
                onChange={(e) => onChange("uiFrequencyKey", e.target.value)}
              >
                {DEFAULT_UI_FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.uiKey} value={opt.uiKey}>
                    {UI_FREQUENCY_LABELS[opt.uiKey] || opt.uiKey}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(limitsMode === "daily" || limitsMode === "daily_monthly") && (
            <div className="re-modal-row">
              <div className="re-modal-field">
                <label className="re-modal-label">Daily limit</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="re-modal-input"
                  placeholder="e.g. 5"
                  value={form.dailyLimit}
                  onChange={(e) => onChange("dailyLimit", e.target.value)}
                  onBlur={(e) => onLimitBlur?.("dailyLimit", e.target.value)}
                />
              </div>
              {limitsMode === "daily_monthly" && (
                <div className="re-modal-field">
                  <label className="re-modal-label">Monthly limit</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="re-modal-input"
                    placeholder="e.g. 50"
                    value={form.monthlyLimit}
                    onChange={(e) => onChange("monthlyLimit", e.target.value)}
                    onBlur={(e) => onLimitBlur?.("monthlyLimit", e.target.value)}
                  />
                  <div className="re-modal-hint">Must be greater than daily limit.</div>
                </div>
              )}
            </div>
          )}

          {limitsMode === "monthly" && (
            <div className="re-modal-field">
              <label className="re-modal-label">Monthly limit</label>
              <input
                type="number"
                className="re-modal-input"
                min={1}
                placeholder="e.g. 50"
                value={form.monthlyLimit}
                onChange={(e) => onChange("monthlyLimit", e.target.value)}
              />
            </div>
          )}

          {limitsMode === "cooldown" && (
            <div className="re-modal-field">
              <label className="re-modal-label">Cooldown (days)</label>
              <input
                type="number"
                className="re-modal-input"
                min={1}
                placeholder="e.g. 90"
                value={form.cooldownDays}
                onChange={(e) => onChange("cooldownDays", e.target.value)}
              />
            </div>
          )}

          <div className="re-modal-field">
            <label className="re-modal-label">Expiry (days)</label>
            <input
              type="text"
              inputMode="numeric"
              className={`re-modal-input${expiryInvalid && expiryTouched ? " re-modal-input-error" : ""}`}
              placeholder="1"
              value={form.expiryDays}
              onChange={(e) => onChange("expiryDays", normalizeCoinDraft(e.target.value))}
              onBlur={() => setExpiryTouched(true)}
            />
            {expiryInvalid && expiryTouched ? (
              <div className="re-modal-error">{expiryWarnings[0]}</div>
            ) : null}
          </div>

          <div className="re-modal-actions">
            <button type="button" className="re-modal-btn cancel" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="re-modal-btn submit"
              disabled={saving || categoriesLoading || !form.category}
            >
              {saving ? "Adding…" : "Add Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NotificationPanel({
  cfg,
  onUpdate,
  onToggleEnable,
  enableLabel = "Enable Notifications for this Event",
}) {
  const enabled = Boolean(cfg.notificationEnabled);
  const titleFilled = Boolean((cfg.notificationTitle || "").trim());
  const bodyFilled = Boolean((cfg.notificationBody || "").trim());
  const canEnable = titleFilled && bodyFilled;
  const toggleDisabled = !enabled && !canEnable;

  const handleToggle = () => {
    if (enabled) {
      onToggleEnable(false);
      return;
    }
    if (!canEnable) {
      toast.error("Fill notification title and body first.");
      return;
    }
    onToggleEnable(true);
  };

  return (
    <div className="re-event-notif">
      <div className="re-event-notif-enable">
        <button
          type="button"
          className={`re-event-notif-switch${enabled ? " on" : ""}${
            toggleDisabled && !enabled ? " locked" : ""
          }`}
          role="switch"
          aria-checked={enabled}
          aria-disabled={toggleDisabled}
          disabled={toggleDisabled}
          onClick={handleToggle}
        >
          <span className="re-event-notif-switch-knob" />
        </button>
        <button
          type="button"
          className="re-event-notif-enable-label"
          disabled={toggleDisabled}
          onClick={handleToggle}
        >
          {enableLabel}
        </button>
      </div>
      {!canEnable && !enabled && (
        <div className="re-event-notif-hint">Fill title and body to enable the toggle.</div>
      )}

      <div className="re-event-notif-field">
        <label className="re-event-notif-label">Notification Title</label>
        <input
          type="text"
          className="re-event-notif-input"
          placeholder="Enter title..."
          value={cfg.notificationTitle || ""}
          onChange={(e) => onUpdate("notificationTitle", e.target.value)}
        />
      </div>

      <div className="re-event-notif-field">
        <label className="re-event-notif-label">Notification Body</label>
        <textarea
          className="re-event-notif-input re-event-notif-textarea"
          placeholder="Enter message body..."
          rows={3}
          value={cfg.notificationBody || ""}
          onChange={(e) => onUpdate("notificationBody", e.target.value)}
        />
      </div>
    </div>
  );
}

function FirstShiftNotificationPanel({ level, onUpdate, onToggleEnable }) {
  const enabled = Boolean(level.isFirstShiftNotificationEnabled);

  const titleFilled = Boolean((level.firstShiftNotificationTitle || "").trim());

  const bodyFilled = Boolean((level.firstShiftNotificationBody || "").trim());

  const canEnable = titleFilled && bodyFilled;
  const toggleDisabled = !enabled && !canEnable;

  const handleToggle = () => {
    if (enabled) {
      onToggleEnable(false);
      return;
    }

    if (!canEnable) {
      toast.error("Fill first-shift notification title and body first.");
      return;
    }

    onToggleEnable(true);
  };

  return (
    <div className="re-event-notif">
      <div className="re-event-notif-enable">
        <button
          type="button"
          className={`re-event-notif-switch${enabled ? " on" : ""}${
            toggleDisabled && !enabled ? " locked" : ""
          }`}
          role="switch"
          aria-checked={enabled}
          aria-disabled={toggleDisabled}
          disabled={toggleDisabled}
          onClick={handleToggle}
        >
          <span className="re-event-notif-switch-knob" />
        </button>

        <button
          type="button"
          className="re-event-notif-enable-label"
          disabled={toggleDisabled}
          onClick={handleToggle}
        >
          Enable First-Shift Notifications
        </button>
      </div>

      {!canEnable && !enabled && (
        <div className="re-event-notif-hint">
          Fill title and body to enable the toggle.
        </div>
      )}

      <div className="re-event-notif-field">
        <label className="re-event-notif-label">
          First-Shift Notification Title
        </label>

        <input
          type="text"
          className="re-event-notif-input"
          placeholder="Enter title..."
          value={level.firstShiftNotificationTitle || ""}
          onChange={(e) =>
            onUpdate("firstShiftNotificationTitle", e.target.value)
          }
        />
      </div>

      <div className="re-event-notif-field">
        <label className="re-event-notif-label">
          First-Shift Notification Body
        </label>

        <textarea
          className="re-event-notif-input re-event-notif-textarea"
          placeholder="Enter message body..."
          rows={3}
          value={level.firstShiftNotificationBody || ""}
          onChange={(e) =>
            onUpdate("firstShiftNotificationBody", e.target.value)
          }
        />
      </div>
    </div>
  );
}

function EventConfigurationSection({
  eventConfig,
  categoryOrder,
  frequencyOptions: frequencyOptionsProp,
  loading,
  saving,
  onUpdate,
  onCoinsBlur,
  onLimitBlur,
  onToggleStatus,
  onEarningFrequencyChange,
  onAddEvent,
  onNotificationToggle,
  
}) {
  const [openNotifKey, setOpenNotifKey] = useState(null);

  const allEntries = useMemo(
    () =>
      categoryOrder.flatMap((cat) =>
        Object.entries(eventConfig).filter(([, cfg]) => cfg.cat === cat)
      ),
    [eventConfig, categoryOrder]
  );

  const frequencyOptions = useMemo(() => {
    const options = [...frequencyOptionsProp];
    const known = new Set(options.map((opt) => opt.uiKey));

    allEntries.forEach(([, cfg]) => {
      const uiKey = getUiFrequencyKey(cfg);
      if (!known.has(uiKey)) {
        options.push({
          uiKey,
          apiFrequency: cfg.earningFrequency,
          label: UI_FREQUENCY_LABELS[uiKey] || cfg.earningFrequency,
          limits: getLimitsMode(cfg, options),
        });
        known.add(uiKey);
      }
    });

    return options;
  }, [allEntries, frequencyOptionsProp]);

  const totalEvents = allEntries.length;
  const totalActive = allEntries.filter(([, c]) => Number(c.coins) > 0).length;

  if (loading) {
    return (
      <div>
        <div className="re-section-head">
          <div className="re-section-title">Event Configuration</div>
        </div>
        <div className="re-loading">Loading events…</div>
      </div>
    );
  }

  return (
    <div>
      <div className="re-section-head">
        <div>
          <div className="re-section-title">
            Event Configuration
            {saving && <span className="re-saving-note"> · Saving…</span>}
          </div>
          <div className="re-section-sub">
            {totalActive} of {totalEvents} events active
          </div>
        </div>
        <button type="button" className="re-add-btn" onClick={onAddEvent}>
          + Add Event
        </button>
      </div>
      {totalEvents === 0 ? (
        <div className="re-empty-note">No events configured.</div>
      ) : (
      <div className="re-table-scroll">
        <table className="re-data-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Event</th>
              <th>Coins</th>
              <th>Frequency</th>
              <th>Limit</th>
              <th>Expiry</th>
              <th className="re-th-notify">Notify</th>
            </tr>
          </thead>
          <tbody>
            {allEntries.map(([key, cfg]) => {
              const isActive = Number(cfg.coins) > 0;
              const warnings = validateEvent(cfg);
              const descMissing = !(cfg.desc || "").trim();
              const notifOpen = openNotifKey === key;
              const notifEnabled = Boolean(cfg.notificationEnabled);
              const frequencyLocked = isBackendHardcodedFrequencyEvent(key);
              return (
                <Fragment key={key}>
                  <tr className={`re-data-row${notifOpen ? " notif-open" : ""}`}>
                    <td>
                      <button
                        type="button"
                        className={`re-status-pill ${isActive ? "on" : "off"}`}
                        onClick={() => onToggleStatus(key)}
                      >
                        {isActive ? "Active" : "Disabled"}
                      </button>
                    </td>
                    <td>
                      <div className="re-system-key">{key}</div>
                      <input
                        type="text"
                        className="re-name-input"
                        title={cfg.name}
                        defaultValue={cfg.name}
                        key={`${key}-name-${cfg.version}`}
                        onBlur={(e) => onUpdate(key, "name", e.target.value)}
                      />
                      <input
                        type="text"
                        className={`re-desc-input${descMissing ? " re-field-error" : ""}`}
                        title={cfg.desc || "Reward History Name"}
                        placeholder="Reward History Name *"
                        value={cfg.desc ?? ""}
                        onChange={(e) => onUpdate(key, "desc", e.target.value)}
                        aria-invalid={descMissing}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="re-cell-input coins"
                        value={cfg.coins === "" || cfg.coins == null ? "" : String(cfg.coins)}
                        onChange={(e) => onUpdate(key, "coins", e.target.value)}
                        onBlur={() => onCoinsBlur?.(key)}
                      />
                    </td>
                    <td>
                      <div className="re-freq-cell">
                        <select
                          className="re-cell-input re-freq-select"
                          value={getUiFrequencyKey(cfg)}
                          disabled={frequencyLocked}
                          onChange={(e) => onEarningFrequencyChange(key, e.target.value)}
                        >
                          {frequencyOptions.map((opt) => (
                            <option key={opt.uiKey} value={opt.uiKey}>
                              {opt.label || UI_FREQUENCY_LABELS[opt.uiKey] || opt.uiKey}
                            </option>
                          ))}
                        </select>
                        {frequencyLocked && <HardcodedFrequencyInfoButton />}
                      </div>
                    </td>
                    <td>
                      <LimitCell
                        eventKey={key}
                        cfg={cfg}
                        onUpdate={onUpdate}
                        onLimitBlur={onLimitBlur}
                        readOnly={frequencyLocked}
                      />
                    </td>
                    <td>
                      <div className="re-limit-group">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="re-cell-input re-limit-input"
                          value={cfg.expiryDays ?? ""}
                          onChange={(e) => onUpdate(key, "expiryDays", e.target.value)}
                        />{" "}
                        days
                      </div>
                    </td>
                    <td className="re-td-notify">
                      <button
                        type="button"
                        className={`re-bell-btn${notifOpen ? " active" : ""}${
                          notifEnabled && !notifOpen ? " enabled" : ""
                        }`}
                        aria-label={`Notification settings for ${key}`}
                        aria-expanded={notifOpen}
                        title="Notification settings"
                        onClick={() => setOpenNotifKey((prev) => (prev === key ? null : key))}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path
                            d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {notifOpen && (
                    <tr className="re-event-notif-row">
                      <td colSpan={7}>
                        <NotificationPanel
                          cfg={cfg}
                          onUpdate={(field, value) => onUpdate(key, field, value)}
                          onToggleEnable={(enabled) => onNotificationToggle?.(key, enabled)}
                        />
                      </td>
                    </tr>
                  )}
                  {warnings.length > 0 && (
                    <tr className="re-warn-row">
                      <td colSpan={7}>⚠ {warnings.join(" ")}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function ReferralLevelsSection({
  levels,
  loading,
  saving,
  onUpdate,
  onReferralFieldBlur,
  onToggleStatus,
  onAddLevel,
  onNotificationToggle,
  onFirstShiftNotificationToggle,
}) {
  const [openNotifLevel, setOpenNotifLevel] = useState(null);
  const [openFirstShiftNotifLevel, setOpenFirstShiftNotifLevel] =
    useState(null);

  return (
    <div>
      <div className="re-section-head">
        <div>
          <div className="re-section-title">
            Referral Levels
            {saving && <span className="re-saving-note"> · Saving…</span>}
          </div>
          <div className="re-section-sub">
            Multi-level referral payout structure · new levels are numbered
            automatically, never typed
          </div>
        </div>
        <button
          type="button"
          className="re-add-btn"
          onClick={onAddLevel}
          disabled={loading || saving}
        >
          + Add Level
        </button>
      </div>
      {loading ? (
        <div className="re-loading">Loading referral levels…</div>
      ) : (
        <div className="re-table-scroll">
          <table className="re-data-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Status</th>
                <th>Coins on signup</th>
                <th>Signup expiry</th>
                <th>First-shift bonus</th>
                <th>First-shift expiry</th>
                <th className="re-th-notify">Notify</th>
              </tr>
            </thead>
            <tbody>
              {levels.length === 0 ? (
                <tr>
                  <td colSpan={7} className="re-empty-note">
                    No referral levels configured.
                  </td>
                </tr>
              ) : (
                levels.map((level) => {
                  const warnings = validateReferralLevel(level);
                  const isActive = level.status === "active";
                  const statusLabel = isActive ? "Active" : "Paused";
                  const notifOpen = openNotifLevel === level.level;
                  const notifEnabled = Boolean(level.notificationEnabled);

                  return (
                    <Fragment key={level._id || level.level}>
                      <tr
                        className={`re-data-row${notifOpen ? " notif-open" : ""}`}
                      >
                        <td>
                          <span className="re-level-tag">L{level.level}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`re-status-pill ${isActive ? "on" : "off"}`}
                            onClick={() => onToggleStatus(level.level)}
                          >
                            {statusLabel}
                          </button>
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="re-cell-input coins"
                            value={
                              level.coins === "" || level.coins == null
                                ? ""
                                : String(level.coins)
                            }
                            onChange={(e) =>
                              onUpdate(level.level, "coins", e.target.value)
                            }
                            onBlur={() =>
                              onReferralFieldBlur?.(level.level, "coins")
                            }
                          />
                        </td>
                        <td>
                          <div className="re-limit-group">
                            <input
                              type="number"
                              className="re-cell-input re-limit-input"
                              placeholder="—"
                              title="Blank = never expires"
                              value={level.signupExpiryDays ?? ""}
                              onChange={(e) =>
                                onUpdate(
                                  level.level,
                                  "signupExpiryDays",
                                  e.target.value,
                                )
                              }
                            />{" "}
                            days
                          </div>
                        </td>
                        {/* <td>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="re-cell-input coins"
                          value={
                            level.firstShiftBonus === "" ||
                            level.firstShiftBonus == null
                              ? ""
                              : String(level.firstShiftBonus)
                          }
                          placeholder="—"
                          onChange={(e) =>
                            onUpdate(
                              level.level,
                              "firstShiftBonus",
                              e.target.value,
                            )
                          }
                          onBlur={() =>
                            onReferralFieldBlur?.(
                              level.level,
                              "firstShiftBonus",
                            )
                          }
                        />

                        <button
                          type="button"
                          className={`re-bell-btn${notifOpen ? " active" : ""}${
                            notifEnabled && !notifOpen ? " enabled" : ""
                          }`}
                          aria-label={`Notification settings for level ${level.level}`}
                          aria-expanded={notifOpen}
                          title="Notification settings"
                          onClick={() =>
                            setOpenNotifLevel((prev) =>
                              prev === level.level ? null : level.level,
                            )
                          }
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden
                          >
                            <path
                              d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                              fill="currentColor"
                            />
                          </svg>
                        </button>
                      </td> */}

                        <td>
                          <div className="re-first-shift-bonus-cell">
                            <input
                              type="text"
                              inputMode="numeric"
                              className="re-cell-input coins"
                              value={
                                level.firstShiftBonus === "" ||
                                level.firstShiftBonus == null
                                  ? ""
                                  : String(level.firstShiftBonus)
                              }
                              placeholder="—"
                              onChange={(e) =>
                                onUpdate(
                                  level.level,
                                  "firstShiftBonus",
                                  e.target.value,
                                )
                              }
                              onBlur={() =>
                                onReferralFieldBlur?.(
                                  level.level,
                                  "firstShiftBonus",
                                )
                              }
                            />

                            <button
                              type="button"
                              className={`re-bell-btn${
                                openFirstShiftNotifLevel === level.level
                                  ? " active"
                                  : ""
                              }${
                                level.isFirstShiftNotificationEnabled &&
                                openFirstShiftNotifLevel !== level.level
                                  ? " enabled"
                                  : ""
                              }`}
                              aria-label={`First-shift notification settings for level ${level.level}`}
                              aria-expanded={
                                openFirstShiftNotifLevel === level.level
                              }
                              title="First-shift notification settings"
                              onClick={() =>
                                setOpenFirstShiftNotifLevel((prev) =>
                                  prev === level.level ? null : level.level,
                                )
                              }
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden
                              >
                                <path
                                  d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                                  fill="currentColor"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>

                        <td>
                          <div className="re-limit-group">
                            <input
                              type="number"
                              className="re-cell-input re-limit-input"
                              placeholder="—"
                              title="Blank = never expires"
                              value={level.firstShiftExpiryDays ?? ""}
                              onChange={(e) =>
                                onUpdate(
                                  level.level,
                                  "firstShiftExpiryDays",
                                  e.target.value,
                                )
                              }
                            />{" "}
                            days
                          </div>
                        </td>
                        <td className="re-td-notify">
                          <button
                            type="button"
                            className={`re-bell-btn${notifOpen ? " active" : ""}${
                              notifEnabled && !notifOpen ? " enabled" : ""
                            }`}
                            aria-label={`Notification settings for level ${level.level}`}
                            aria-expanded={notifOpen}
                            title="Notification settings"
                            onClick={() =>
                              setOpenNotifLevel((prev) =>
                                prev === level.level ? null : level.level,
                              )
                            }
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden
                            >
                              <path
                                d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                                fill="currentColor"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                      {notifOpen && (
                        <tr className="re-event-notif-row">
                          <td colSpan={7}>
                            <NotificationPanel
                              cfg={level}
                              enableLabel="Enable Notifications for this Level"
                              onUpdate={(field, value) =>
                                onUpdate(level.level, field, value)
                              }
                              onToggleEnable={(enabled) =>
                                onNotificationToggle?.(level.level, enabled)
                              }
                            />
                          </td>
                        </tr>
                      )}

                      {openFirstShiftNotifLevel === level.level && (
                        <tr className="re-event-notif-row">
                          <td colSpan={6}>
                            <FirstShiftNotificationPanel
                              level={level}
                              onUpdate={(field, value) =>
                                onUpdate(level.level, field, value)
                              }
                              onToggleEnable={(enabled) =>
                                onFirstShiftNotificationToggle?.(
                                  level.level,
                                  enabled,
                                )
                              }
                            />
                          </td>
                        </tr>
                      )}
                      {warnings.length > 0 && (
                        <tr className="re-warn-row">
                          <td colSpan={7}>⚠ {warnings.join(" ")}</td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AuditLogSection({ auditLog, loading, hasMore, loadingMore, onLoadMore }) {
  return (
    <div className="re-card re-settings-card">
      <div className="re-settings-card-title">Audit Log</div>
      <div className="re-settings-desc" style={{ marginBottom: 10 }}>
        Every change made in this console — who, what, when
      </div>
      {loading ? (
        <div className="re-loading">Loading audit log…</div>
      ) : (
        <>
          <div className="re-audit-log-list">
            {auditLog.length === 0 ? (
              <div className="re-empty-note">No changes recorded yet.</div>
            ) : (
              auditLog.map((entry) => (
                <div key={entry._id || `${entry.at}-${entry.summary}`} className="re-audit-log-row">
                  <span className="t">{fmtTime(entry.at)}</span>
                  <span className="a">{entry.actor}</span>
                  <span className="s">{entry.summary}</span>
                </div>
              ))
            )}
          </div>
          {hasMore && (
            <button
              type="button"
              className="re-add-btn"
              style={{ marginTop: 12 }}
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function RewardsEngine() {
  const [globalSettings, setGlobalSettings] = useState(null);
  const [globalSettingsLoading, setGlobalSettingsLoading] = useState(true);
  const globalSettingsRef = useRef(null);
  const eventConfigRef = useRef({});
  const referralLevelsRef = useRef([]);
  const savedGlobalSettingsRef = useRef(null);
  const savedEventConfigRef = useRef({});
  const savedReferralLevelsRef = useRef([]);

  const [eventConfig, setEventConfig] = useState({});
  const [eventConfigLoading, setEventConfigLoading] = useState(true);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [referralLevels, setReferralLevels] = useState([]);
  const [referralLevelsLoading, setReferralLevelsLoading] = useState(true);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(true);
  const [auditLogLoadingMore, setAuditLogLoadingMore] = useState(false);
  const [auditLogHasMore, setAuditLogHasMore] = useState(false);
  const auditLogPageRef = useRef(1);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [addEventModalOpen, setAddEventModalOpen] = useState(false);
  const [addEventSaving, setAddEventSaving] = useState(false);
  const [addEventForm, setAddEventForm] = useState(createEmptyAddEventForm);
  const [categoryEventNames, setCategoryEventNames] = useState([]);
  const [categoryEventsLoading, setCategoryEventsLoading] = useState(true);
  const rewardsRootRef = useRef(null);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const fetchAuditLogs = useCallback(async ({ page = 1, append = false } = {}) => {
    if (append) {
      setAuditLogLoadingMore(true);
    } else {
      setAuditLogLoading(true);
    }

    const payload = getAuditLogListPayload(page);
    const response = await getAuditLogList(payload);

    if (append) {
      setAuditLogLoadingMore(false);
    } else {
      setAuditLogLoading(false);
    }

    if (response?.error) {
      toast.error(response.error);
      if (!append) setAuditLog([]);
      return;
    }

    const entries = mapAuditLogsFromListResponse(response);
    const pagination = extractAuditLogPagination(response, payload);

    setAuditLog((prev) => (append ? [...prev, ...entries] : entries));
    auditLogPageRef.current = pagination.page;
    setAuditLogHasMore(pagination.hasMore);
  }, []);

  const refreshAuditLogs = useCallback(() => {
    fetchAuditLogs({ page: 1, append: false });
  }, [fetchAuditLogs]);

  const handleAuditLogLoadMore = () => {
    fetchAuditLogs({ page: auditLogPageRef.current + 1, append: true });
  };

  const fetchCategoryEvents = useCallback(async () => {
    setCategoryEventsLoading(true);
    const response = await getCategoryEventList();
    setCategoryEventsLoading(false);

    if (response?.error) {
      toast.error(response.error);
      setCategoryEventNames([]);
      return;
    }

    const names = mapCategoryEventNamesFromResponse(response);
    setCategoryEventNames(names);
    setAddEventForm((prev) =>
      prev.category || names.length === 0 ? prev : { ...prev, category: names[0] }
    );
  }, []);

  useEffect(() => {
    globalSettingsRef.current = globalSettings;
  }, [globalSettings]);

  useEffect(() => {
    eventConfigRef.current = eventConfig;
  }, [eventConfig]);

  useEffect(() => {
    referralLevelsRef.current = referralLevels;
  }, [referralLevels]);

  const fetchGlobalSettings = useCallback(async () => {
    setGlobalSettingsLoading(true);
    const response = await getReferralSettingsList();
    setGlobalSettingsLoading(false);

    if (response?.error) {
      toast.error(response.error);
      setGlobalSettings(null);
      return;
    }

    const settings = extractGlobalSettingsFromListResponse(response);
    if (!settings) {
      toast.error("Global settings not found");
      setGlobalSettings(null);
      return;
    }

    setGlobalSettings(settings);
    savedGlobalSettingsRef.current = cloneState(settings);
  }, []);

  const fetchRewardEvents = useCallback(async () => {
    setEventConfigLoading(true);
    const response = await getRewardEventList();
    setEventConfigLoading(false);

    if (response?.error) {
      toast.error(response.error);
      setEventConfig({});
      setCategoryOrder([]);
      return;
    }

    const events = mapEventsFromListResponse(response);
    const eventMap = buildEventConfigMap(events);
    setFrequencyOptions(buildFrequencyOptionsFromEvents(events));
    setEventConfig(eventMap);
    setCategoryOrder(buildCategoryOrderFromEvents(events));
    savedEventConfigRef.current = cloneState(eventMap);
  }, []);

  const fetchReferralLevels = useCallback(async () => {
    setReferralLevelsLoading(true);
    const response = await getReferralLevelList();
    setReferralLevelsLoading(false);

    if (response?.error) {
      toast.error(response.error);
      setReferralLevels([]);
      return;
    }

    const levels = mapReferralLevelsFromListResponse(response);
    setReferralLevels(levels);
    savedReferralLevelsRef.current = cloneState(levels);
  }, []);

  useEffect(() => {
    fetchGlobalSettings();
    fetchRewardEvents();
    fetchReferralLevels();
    fetchCategoryEvents();
    fetchAuditLogs({ page: 1 });
  }, [
    fetchGlobalSettings,
    fetchRewardEvents,
    fetchReferralLevels,
    fetchCategoryEvents,
    fetchAuditLogs,
  ]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Block mouse-wheel / arrow-key value changes on numeric inputs
  useEffect(() => {
    const root = rewardsRootRef.current;
    if (!root) return undefined;

    const onWheel = (event) => {
      if (!isNumericField(event.target)) return;
      event.preventDefault();
      event.target.blur();
    };

    const onKeyDown = (event) => {
      if (!isNumericField(event.target)) return;
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    };

    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("keydown", onKeyDown);
    return () => {
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const handleGlobalChange = (newSettings) => {
    setGlobalSettings(newSettings);
    markDirty();
  };

  const handleEventUpdate = (eventKey, field, value) => {
    const current = eventConfigRef.current[eventKey];
    if (!current) return;

    const nextCfg = applyEventFieldUpdate(current, field, value);
    setEventConfig((prev) => ({ ...prev, [eventKey]: nextCfg }));

    if (
      field === "notificationTitle" ||
      field === "notificationBody" ||
      field === "notificationEnabled"
    ) {
      markDirty();
      return;
    }

    markDirty();
  };

  const handleEventLimitBlur = (eventKey, field, rawValue) => {
    const current = eventConfigRef.current[eventKey];
    if (!current || isBackendHardcodedFrequencyEvent(eventKey)) return;

    const nextCfg = applyEventLimitBlur(current, field, rawValue);
    setEventConfig((prev) => ({ ...prev, [eventKey]: nextCfg }));
    markDirty();
  };

  const handleNotificationToggle = (eventKey, enabled) => {
    const current = eventConfigRef.current[eventKey];
    if (!current) return;

    if (enabled) {
      const title = (current.notificationTitle || "").trim();
      const body = (current.notificationBody || "").trim();
      if (!title || !body) {
        toast.error("Fill notification title and body first.");
        return;
      }
    }

    const nextCfg = applyEventFieldUpdate(current, "notificationEnabled", enabled);
    setEventConfig((prev) => ({ ...prev, [eventKey]: nextCfg }));
    markDirty();
  };

  const handleCoinsBlur = (eventKey) => {
    const current = eventConfigRef.current[eventKey];
    if (!current) return;

    const coinValue = coinDraftToNumber(current.coins);
    const nextCfg = {
      ...current,
      coins: coinValue === 0 ? 0 : String(coinValue),
      version: (current.version || 1) + 1,
    };

    if (coinValue === 0 && Number(current._lastCoins) > 0) {
      nextCfg._lastCoins = current._lastCoins;
    } else if (coinValue > 0) {
      delete nextCfg._lastCoins;
    }

    setEventConfig((prev) => ({ ...prev, [eventKey]: nextCfg }));
  };

  const handleEventToggle = (eventKey) => {
    const current = eventConfigRef.current[eventKey];
    if (!current) return;

    const nextCfg = applyEventToggle(current, DEFAULT_ACTIVE_COINS);
    setEventConfig((prev) => ({ ...prev, [eventKey]: nextCfg }));
    markDirty();
  };

  const handleEarningFrequencyChange = (eventKey, uiKey) => {
    if (isBackendHardcodedFrequencyEvent(eventKey)) return;

    const current = eventConfigRef.current[eventKey];
    if (!current) return;

    const nextCfg = applyEarningFrequencyUpdate(current, uiKey);
    setEventConfig((prev) => ({ ...prev, [eventKey]: nextCfg }));
    markDirty();
  };

  const handleOpenAddEventModal = () => {
    setAddEventForm(createEmptyAddEventForm(categoryEventNames));
    setAddEventModalOpen(true);
  };

  const handleCloseAddEventModal = () => {
    if (addEventSaving) return;
    setAddEventModalOpen(false);
  };

  const handleAddEventFormChange = (field, value) => {
    setAddEventForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddEventLimitBlur = (field, rawValue) => {
    setAddEventForm((prev) => {
      const limitsMode = getLimitsModeForUiKey(prev.uiFrequencyKey);
      let dailyLimit = prev.dailyLimit;
      let monthlyLimit = prev.monthlyLimit;

      if (field === "dailyLimit") {
        const daily = finalizeDailyLimit(rawValue);
        dailyLimit = daily == null ? "" : String(daily);
        if (limitsMode === "daily_monthly" && monthlyLimit !== "" && monthlyLimit != null) {
          const monthly = finalizeMonthlyLimit(monthlyLimit, daily);
          monthlyLimit = monthly == null ? "" : String(monthly);
        }
      } else if (field === "monthlyLimit") {
        const daily = finalizeDailyLimit(prev.dailyLimit);
        if (limitsMode === "daily" || limitsMode === "daily_monthly") {
          dailyLimit = daily == null ? "" : String(daily);
        }
        const monthly = finalizeMonthlyLimit(rawValue, daily);
        monthlyLimit = monthly == null ? "" : String(monthly);
      }

      return { ...prev, dailyLimit, monthlyLimit };
    });
  };

  const handleSubmitAddEvent = async (e) => {
    e.preventDefault();

    const trimmedName = addEventForm.name.trim();
    if (!trimmedName) {
      toast.error("Event name is required.");
      return;
    }

    const trimmedDesc = addEventForm.desc.trim();
    if (!trimmedDesc) {
      toast.error("Reward History Name is required.");
      return;
    }

    const eventKey = addEventForm.category?.trim();
    if (!eventKey) {
      toast.error("Category is required.");
      return;
    }
    const category = eventKey;
    const coinValue = coinDraftToNumber(addEventForm.coins);
    const expiryWarnings = validateEventExpiry({ expiryDays: addEventForm.expiryDays });
    if (expiryWarnings.length) {
      toast.error(expiryWarnings[0]);
      return;
    }
    const expiryDays = finalizeExpiryDays(addEventForm.expiryDays);

    const limitsMode = getLimitsModeForUiKey(addEventForm.uiFrequencyKey);
    const dailyLimit =
      limitsMode === "daily" || limitsMode === "daily_monthly"
        ? finalizeDailyLimit(addEventForm.dailyLimit)
        : null;
    const monthlyLimit =
      limitsMode === "monthly" || limitsMode === "daily_monthly"
        ? finalizeMonthlyLimit(addEventForm.monthlyLimit, dailyLimit)
        : null;

    const freqCfg = applyEarningFrequencyUpdate(
      {
        dailyLimit,
        monthlyLimit,
        cooldownDays:
          addEventForm.cooldownDays === "" ? null : parseWholeNumber(addEventForm.cooldownDays, { min: 1 }),
      },
      addEventForm.uiFrequencyKey
    );
    const capWarnings = validateEventCaps(freqCfg);
    if (capWarnings.length) {
      toast.error(capWarnings[0]);
      return;
    }
    const freqPayload = mapEarningFrequencyPayload(freqCfg, addEventForm.uiFrequencyKey);

    setAddEventSaving(true);
    const response = await createRewardEvent(
      mapEventToCreateApi({
        eventKey,
        name: trimmedName,
        description: trimmedDesc,
        coinValue,
        earningFrequency: freqPayload.earningFrequency,
        dailyLimit: freqPayload.dailyLimit,
        monthlyLimit: freqPayload.monthlyLimit,
        cooldownDays: freqPayload.cooldownDays,
        expiryDays,
      })
    );
    setAddEventSaving(false);

    if (response?.error) {
      toast.error(response.error);
      return;
    }

    const created = mapEventFromMutationResponse(response);
    if (!created) {
      toast.error("Failed to create event");
      await fetchRewardEvents();
      return;
    }

    const withCategory = { ...created, cat: created.cat || category };
    setEventConfig((prev) => {
      const next = { ...prev, [withCategory.eventKey]: withCategory };
      savedEventConfigRef.current = {
        ...savedEventConfigRef.current,
        [withCategory.eventKey]: cloneState(withCategory),
      };
      return next;
    });
    setCategoryOrder((prev) =>
      prev.includes(withCategory.cat) ? prev : [...prev, withCategory.cat]
    );
    setAddEventModalOpen(false);
    refreshAuditLogs();
  };

  const handleLevelUpdate = (levelNum, field, value) => {
    const current = referralLevelsRef.current.find((level) => level.level === levelNum);
    if (!current?._id) return;

    const nextLevel = applyReferralLevelFieldUpdate(current, field, value);
    setReferralLevels((prev) =>
      prev.map((level) => (level._id === current._id ? nextLevel : level))
    );
    markDirty();
  };

  const handleReferralNotificationToggle = (levelNum, enabled) => {
    const current = referralLevelsRef.current.find((level) => level.level === levelNum);
    if (!current) return;

    if (enabled) {
      const title = (current.notificationTitle || "").trim();
      const body = (current.notificationBody || "").trim();
      if (!title || !body) {
        toast.error("Fill notification title and body first.");
        return;
      }
    }

    const nextLevel = applyReferralLevelFieldUpdate(current, "notificationEnabled", enabled);
    setReferralLevels((prev) =>
      prev.map((level) => (level._id === current._id ? nextLevel : level))
    );
    markDirty();
  };

  const handleFirstShiftNotificationToggle = (levelNum, enabled) => {
  const current = referralLevelsRef.current.find(
    (level) => level.level === levelNum
  );

  if (!current) return;

  if (enabled) {
    const title = (current.firstShiftNotificationTitle || "").trim();
    const body = (current.firstShiftNotificationBody || "").trim();

    if (!title || !body) {
      toast.error(
        "Fill first-shift notification title and body first."
      );
      return;
    }
  }

  const nextLevel = applyReferralLevelFieldUpdate(
    current,
    "isFirstShiftNotificationEnabled",
    enabled
  );

  setReferralLevels((prev) =>
    prev.map((level) =>
      level._id === current._id ? nextLevel : level
    )
  );

  markDirty();
};

  const handleReferralFieldBlur = (levelNum, field) => {
    if (field !== "coins" && field !== "firstShiftBonus") return;

    const current = referralLevelsRef.current.find((level) => level.level === levelNum);
    if (!current?._id) return;

    let nextLevel = { ...current, version: (current.version || 1) + 1 };

    if (field === "coins") {
      const coinValue = coinDraftToNumber(current.coins);
      nextLevel.coins = coinValue === 0 ? 0 : String(coinValue);
      nextLevel.status = deriveLevelStatus(coinValue, current.status);
      nextLevel.isActive = nextLevel.status === "active";
    } else {
      const bonusValue =
        current.firstShiftBonus === "" || current.firstShiftBonus == null
          ? null
          : coinDraftToNumber(current.firstShiftBonus);
      nextLevel.firstShiftBonus = bonusValue;
    }

    setReferralLevels((prev) =>
      prev.map((level) => (level._id === current._id ? nextLevel : level))
    );
  };

  const handleLevelToggle = (levelNum) => {
    const current = referralLevelsRef.current.find((level) => level.level === levelNum);
    if (!current?._id) return;

    if (current.status !== "active") {
      if (coinDraftToNumber(current.coins) <= 0) {
        toast.error(`Level ${levelNum} can't be activated with 0 coins. Set a coin value first.`);
        return;
      }
    }

    const nextLevel = applyReferralLevelToggle(current);
    setReferralLevels((prev) =>
      prev.map((level) => (level._id === current._id ? nextLevel : level))
    );
    markDirty();
  };

  const handleAddLevel = async () => {
    setIsSaving(true);
    const response = await createReferralLevel(
      mapReferralLevelToCreateApi({
        coins: 1,
        signupExpiryDays: null,
        firstShiftBonus: null,
        firstShiftExpiryDays: null,
      })
    );
    setIsSaving(false);

    if (response?.error) {
      toast.error(response.error);
      return;
    }

    const created = mapReferralLevelFromMutationResponse(response);
    if (!created) {
      toast.error("Failed to create referral level");
      return;
    }

    setReferralLevels((prev) => {
      const next = [...prev, created].sort((a, b) => a.level - b.level);
      savedReferralLevelsRef.current = cloneState(next);
      return next;
    });
    refreshAuditLogs();
  };

  const handleCancel = () => {
    setGlobalSettings(cloneState(savedGlobalSettingsRef.current));
    setEventConfig(cloneState(savedEventConfigRef.current) || {});
    setReferralLevels(cloneState(savedReferralLevelsRef.current) || []);
    setIsDirty(false);
  };

  const handleSave = async () => {
    if (!isDirty || isSaving) return;

    const referralCap = Number(globalSettingsRef.current?.monthlyReferralCoinCap);
    if (!Number.isInteger(referralCap) || referralCap < 0) {
      toast.error("Monthly referral coin cap must be a whole number 0 or greater.");
      return;
    }

    const blankHistoryName = Object.entries(eventConfigRef.current).find(
      ([, cfg]) => !(cfg.desc || "").trim()
    );
    if (blankHistoryName) {
      toast.error(`${blankHistoryName[0]}: Reward History Name is required.`);
      return;
    }

    const normalizedEvents = Object.fromEntries(
      Object.entries(eventConfigRef.current).map(([key, cfg]) => {
        const coins = coinDraftToNumber(cfg.coins);
        let next = { ...cfg, coins: coins === 0 ? 0 : String(coins) };
        const mode = getLimitsMode(next);
        if (
          !isBackendHardcodedFrequencyEvent(key) &&
          (mode === "daily" || mode === "daily_monthly")
        ) {
          next = applyEventLimitBlur(next, "dailyLimit", next.dailyLimit ?? "");
          if (mode === "daily_monthly") {
            next = applyEventLimitBlur(next, "monthlyLimit", next.monthlyLimit ?? "");
          }
        }
        return [key, next];
      })
    );

    for (const [eventKey, cfg] of Object.entries(normalizedEvents)) {
      if (isBackendHardcodedFrequencyEvent(eventKey)) continue;
      const eventWarnings = validateEvent(cfg);
      if (eventWarnings.length) {
        toast.error(`${eventKey}: ${eventWarnings[0]}`);
        return;
      }
      if (cfg.notificationEnabled) {
        if (!(cfg.notificationTitle || "").trim() || !(cfg.notificationBody || "").trim()) {
          toast.error(`${eventKey}: Notification title and body are required when enabled.`);
          return;
        }
      }
    }

    for (const level of referralLevelsRef.current) {
      const levelWarnings = validateReferralLevel(level);
      if (levelWarnings.length) {
        toast.error(`Level ${level.level}: ${levelWarnings[0]}`);
        return;
      }
    }

    const normalizedLevels = referralLevelsRef.current.map((level) => {
      const coins = coinDraftToNumber(level.coins);
      const firstShiftBonus =
        level.firstShiftBonus === "" || level.firstShiftBonus == null
          ? null
          : coinDraftToNumber(level.firstShiftBonus);
      const status = deriveLevelStatus(coins, level.status);
      return {
        ...level,
        coins: coins === 0 ? 0 : String(coins),
        firstShiftBonus,
        status,
        isActive: status === "active",
      };
    });

    setEventConfig(normalizedEvents);
    setReferralLevels(normalizedLevels);

    setIsSaving(true);
    const errors = [];
    let nextGlobal = {
      ...globalSettingsRef.current,
      monthlyReferralCoinCap: referralCap,
    };
    setGlobalSettings(nextGlobal);
    const nextEvents = { ...normalizedEvents };
    const nextLevels = [...normalizedLevels];

    if (
      nextGlobal &&
      !signaturesEqual(
        getGlobalSettingsSaveSignature(nextGlobal),
        getGlobalSettingsSaveSignature(savedGlobalSettingsRef.current)
      )
    ) {
      const response = await updateReferralSettings(mapGlobalSettingsToApi(nextGlobal));
      if (response?.error) {
        errors.push(response.error);
      } else {
        const updated =
          extractGlobalSettingsFromUpdateResponse(response) ||
          extractGlobalSettingsFromListResponse(response);
        nextGlobal = updated || nextGlobal;
        setGlobalSettings(nextGlobal);
      }
    }

    const savedEvents = savedEventConfigRef.current || {};
    for (const [eventKey, cfg] of Object.entries(nextEvents)) {
      if (!cfg?._id) continue;
      const saved = savedEvents[eventKey];
      if (signaturesEqual(getEventSaveSignature(cfg), getEventSaveSignature(saved))) continue;

      const response = await updateRewardEvent(cfg._id, mapEventToUpdateApi(cfg));
      if (response?.error) {
        errors.push(`${eventKey}: ${response.error}`);
        continue;
      }
      const updated = mapEventFromMutationResponse(response);
      if (updated) {
        nextEvents[eventKey] = mergeEventAfterUpdate(cfg, updated);
      }
    }
    setEventConfig({ ...nextEvents });

    const savedLevels = savedReferralLevelsRef.current || [];
    for (let i = 0; i < nextLevels.length; i += 1) {
      const level = nextLevels[i];
      if (!level?._id) continue;
      const saved = savedLevels.find((item) => item._id === level._id);
      if (signaturesEqual(getReferralLevelSaveSignature(level), getReferralLevelSaveSignature(saved))) {
        continue;
      }

      const response = await updateReferralLevel(level._id, mapReferralLevelToUpdateApi(level));
      if (response?.error) {
        errors.push(`Level ${level.level}: ${response.error}`);
        continue;
      }
      const updated = mapReferralLevelFromMutationResponse(response);
      if (updated) {
        nextLevels[i] = mergeReferralLevelAfterUpdate(level, updated);
      }
    }
    setReferralLevels(nextLevels);
    setIsSaving(false);

    if (errors.length > 0) {
      toast.error(errors[0]);
      setIsDirty(true);
      return;
    }

    savedGlobalSettingsRef.current = cloneState(nextGlobal);
    savedEventConfigRef.current = cloneState(nextEvents);
    savedReferralLevelsRef.current = cloneState(nextLevels);
    setIsDirty(false);
    toast.success("Changes saved");
    refreshAuditLogs();
  };

  const lastUpdatedText =
    auditLog.length > 0
      ? `Last updated ${fmtTime(auditLog[0].at)} by ${auditLog[0].actor}`
      : globalSettings?.updatedAt
        ? `Last updated ${fmtTime(globalSettings.updatedAt)}`
        : "No changes recorded yet";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="rewards-engine" ref={rewardsRootRef}>
        <div className="re-topbar">
          <div className="re-topbar-row">
            <div>
              <div className="re-topbar-title">
                Coin &amp; Rewards Rules Engine
              </div>
              <div className="re-topbar-sub">{lastUpdatedText}</div>
            </div>
          </div>
        </div>

        <div className="re-content">
          <GlobalSettingsSection
            settings={globalSettings}
            loading={globalSettingsLoading}
            saving={false}
            onChange={handleGlobalChange}
          />

          <EventConfigurationSection
            eventConfig={eventConfig}
            categoryOrder={categoryOrder}
            frequencyOptions={frequencyOptions}
            loading={eventConfigLoading}
            saving={false}
            onUpdate={handleEventUpdate}
            onCoinsBlur={handleCoinsBlur}
            onLimitBlur={handleEventLimitBlur}
            onToggleStatus={handleEventToggle}
            onEarningFrequencyChange={handleEarningFrequencyChange}
            onAddEvent={handleOpenAddEventModal}
            onNotificationToggle={handleNotificationToggle}
          />

          <ReferralLevelsSection
            levels={referralLevels}
            loading={referralLevelsLoading}
            saving={isSaving}
            onUpdate={handleLevelUpdate}
            onReferralFieldBlur={handleReferralFieldBlur}
            onToggleStatus={handleLevelToggle}
            onAddLevel={handleAddLevel}
            onNotificationToggle={handleReferralNotificationToggle}
            onFirstShiftNotificationToggle={handleFirstShiftNotificationToggle}
          />

          <AuditLogSection
            auditLog={auditLog}
            loading={auditLogLoading}
            hasMore={auditLogHasMore}
            loadingMore={auditLogLoadingMore}
            onLoadMore={handleAuditLogLoadMore}
          />

          <div className="re-footer-actions">
            <button
              type="button"
              className="re-action-btn cancel"
              onClick={handleCancel}
              disabled={!isDirty || isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="re-action-btn save"
              onClick={handleSave}
              disabled={!isDirty || isSaving}
            >
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <AddEventModal
          open={addEventModalOpen}
          form={addEventForm}
          categoryNames={categoryEventNames}
          categoriesLoading={categoryEventsLoading}
          saving={addEventSaving}
          onChange={handleAddEventFormChange}
          onLimitBlur={handleAddEventLimitBlur}
          onCancel={handleCloseAddEventModal}
          onSubmit={handleSubmitAddEvent}
        />
      </div>
    </DashboardLayout>
  );
}

export default RewardsEngine;
